/**
 * CHIBRE ARENA ("Block City") — city-block realism pass (2026-11-04, owner:
 * "improve each map", "realistic", "get inspired by existing models, don't
 * copy them").
 *
 * The map is the BLOCK CITY bomb-battle layout: eight beige towers and kiosks
 * on one flat slab inside a 16 m perimeter wall. The renderer already gives
 * the towers window facades, but the slab is one tiling texture with nothing
 * between "road" and "building", the towers meet the ground with a knife
 * edge, and the perimeter is a blank wall. The towers keep the renderer's
 * own dressing; this pass owns the ground and the perimeter. Inspiration (not copies): Paris
 * street photography and the usual urban prop kits — asphalt carriageways
 * with granite kerbs and concrete pavements hugging every block, cast-iron
 * manholes and gutter grates, stone plinths, string courses and cornices,
 * cast-iron downpipes, wall lanterns and enamel street plaques.
 *
 * Everything sits on/inside the shared colliders (cityDressing.ts contract,
 * checked by agent/bench-extras-chibre.mjs). ≤ 7 draw calls, built once.
 */
import type * as THREE from "three";
import type { MapDef } from "@neon-purge/shared";
import type { MapExtrasContext } from "../mapExtras.js";
import { ATLAS, buildPieces, dressFacade, floorCells, h01, kerbRing, placeOrSplit, Scene, streetClutter, type FacadeStyle, type Piece } from "../cityDressing.js";
export { WALL_PROUD, GROUND_LIFT, TOP_LIFT } from "../cityDressing.js";

export const mapIds = ["chibre_arena"];

const C = {
  asphalt: 0x5a5c5f, asphalt2: 0x515356, pave: 0xbdb6aa, pave2: 0xb0a99d,
};
const STYLE: FacadeStyle = {
  plinth: 0x8a8174, plinth2: 0x81796c, cornice: 0xd2c8b6, cornice2: 0xc3b9a7, string: 0xc9bfad,
  pipe: 0x3a3e42, lamp: 0x2a2c2e, plaque: ATLAS.plaqueRue, lanterns: true,
};
const SIDEWALK = 2.6;
const KERB = 0.3;

export function cityLayout(map: MapDef, lowSpec: boolean): Piece[] {
  const S = new Scene(map);
  const boxes = map.boxes;
  const isBlock = (i: number): boolean => !S.floors.has(i) && S.A[i].y0 < 0.5 && !S.isWall(i);
  const blocks = boxes.map((_, i) => i).filter(isBlock);
  const P = S.play;
  const inPavement = (x: number, z: number, skip: number): boolean => {
    if (x < P.x0 + SIDEWALK || x > P.x1 - SIDEWALK || z < P.z0 + SIDEWALK || z > P.z1 - SIDEWALK) return skip !== -2;
    for (const i of blocks) {
      if (i === skip) continue;
      const a = S.A[i];
      if (x > a.x0 - SIDEWALK && x < a.x1 + SIDEWALK && z > a.z0 - SIDEWALK && z < a.z1 + SIDEWALK) return true;
    }
    return false;
  };

  // ── GROUND: asphalt carriageway + pavements around every block + the walls
  floorCells(S, SIDEWALK, (i) => isBlock(i) || S.isWall(i), (x0, x1, z0, z1, near) => {
    const k = h01(Math.round(x0 * 7), Math.round(z0 * 7), 3);
    if (near) S.ground("pave", "pavement", x0, x1, z0, z1, 0.045, k < 0.5 ? C.pave : C.pave2, { tile: 1.8 });
    else S.ground("asphalt", "asphalt", x0, x1, z0, z1, 0.012, k < 0.5 ? C.asphalt : C.asphalt2, { tile: 4 });
  });
  // perimeter pavement: the play rect border strip is "near" the walls already
  const near = (i: number) => (x: number, z: number): boolean => inPavement(x, z, i);
  for (const i of blocks) kerbRing(S, S.A[i], SIDEWALK, KERB, near(i), [0x8f8c86, 0x9a968f]);
  kerbRing(S, { x0: P.x0 + 2 * SIDEWALK, x1: P.x1 - 2 * SIDEWALK, z0: P.z0 + 2 * SIDEWALK, z1: P.z1 - 2 * SIDEWALK }, SIDEWALK, KERB,
    (x, z) => inPavement(x, z, -2), [0x8f8c86, 0x9a968f]);
  // lane paint: every street corridor between two facing blocks (or a block
  // and the perimeter pavement) gets a dashed centre line and a zebra
  // crossing at each end of the corridor
  const wornWhite = [...ATLAS.wornWhite] as [number, number, number, number];
  const rects = blocks.map((i) => S.A[i]);
  const edges = [
    ...rects,
    { x0: -1e3, x1: P.x0, z0: P.z0, z1: P.z1 }, { x0: P.x1, x1: 1e3, z0: P.z0, z1: P.z1 },
    { x0: P.x0, x1: P.x1, z0: -1e3, z1: P.z0 }, { x0: P.x0, x1: P.x1, z0: P.z1, z1: 1e3 },
  ];
  const seen = new Set<string>();
  for (let a = 0; a < edges.length; a++) for (let b = 0; b < edges.length; b++) {
    if (a === b) continue;
    const A = edges[a], B = edges[b];
    for (const ax of ["x", "z"] as const) {
      // B lies past A along `ax`; the corridor runs along the other axis
      const lo = ax === "x" ? A.x1 : A.z1, hi = ax === "x" ? B.x0 : B.z0;
      const gap = hi - lo;
      if (gap < 9 || gap > 26) continue;
      const s0 = Math.max(ax === "x" ? A.z0 : A.x0, ax === "x" ? B.z0 : B.x0);
      const s1 = Math.min(ax === "x" ? A.z1 : A.x1, ax === "x" ? B.z1 : B.x1);
      if (s1 - s0 < 8) continue;
      const key = `${ax}|${lo.toFixed(1)}|${hi.toFixed(1)}|${s0.toFixed(1)}`;
      if (seen.has(key)) continue;
      seen.add(key);
      const mid = (lo + hi) / 2;
      // nothing standing in the corridor between them (else it is not a street)
      const probe = ax === "x" ? { x0: lo + 0.1, x1: hi - 0.1, z0: s0, z1: s1, y0: 0, y1: 2 } : { x0: s0, x1: s1, z0: lo + 0.1, z1: hi - 0.1, y0: 0, y1: 2 };
      if (!S.clear(probe)) continue;
      const road0 = lo + SIDEWALK + 0.4, road1 = hi - SIDEWALK - 0.4;
      for (let t = s0 + 4.5; t + 3 < s1 - 4.5; t += 6) {
        if (ax === "x") S.ground("paint", "dash", mid - 0.08, mid + 0.08, t, t + 3, 0.016, 0xffffff, { quad: true, uv: wornWhite });
        else S.ground("paint", "dash", t, t + 3, mid - 0.08, mid + 0.08, 0.016, 0xffffff, { quad: true, uv: wornWhite });
      }
      if (lowSpec) continue;
      // zebra: 0.5 m bars across the carriageway, 2.6 m long along it
      for (const end of [s0 + 1.0, s1 - 3.6]) {
        for (let w = road0; w + 0.5 <= road1; w += 1.0) {
          if (ax === "x") S.ground("paint", "zebra", w, w + 0.5, end, end + 2.6, 0.017, 0xffffff, { quad: true, uv: wornWhite });
          else S.ground("paint", "zebra", end, end + 2.6, w, w + 0.5, 0.017, 0xffffff, { quad: true, uv: wornWhite });
        }
      }
    }
  }
  streetClutter(S, lowSpec ? 10 : 28, SIDEWALK + 0.4, 21, ["manhole", "crack", "manhole", "crack", "grate"]);
  // gutter grates along the kerbs (just on the road side)
  if (!lowSpec) for (const i of blocks) {
    const a = S.A[i];
    for (const [x, z, alongX] of [
      [(a.x0 + a.x1) / 2, a.z0 - SIDEWALK - 0.4, true], [(a.x0 + a.x1) / 2, a.z1 + SIDEWALK + 0.4, true],
      [a.x0 - SIDEWALK - 0.4, (a.z0 + a.z1) / 2, false], [a.x1 + SIDEWALK + 0.4, (a.z0 + a.z1) / 2, false],
    ] as [number, number, boolean][]) {
      if (inPavement(x, z, -1)) continue;
      const hw = alongX ? 0.45 : 0.22, hd = alongX ? 0.22 : 0.45;
      S.ground("paint", "grate", x - hw, x + hw, z - hd, z + hd, 0.022, 0xffffff, { quad: true, uv: [...ATLAS.grate] as [number, number, number, number] });
    }
  }

  // ── PERIMETER: the renderer dresses the towers (windows, pilasters,
  // shopfronts, parapets, tanks) but leaves the 16 m perimeter as a blank
  // render wall. Give it the street-facing skin of a party wall: rusticated
  // plinth, string course + cornice, downpipes, lanterns, street plaques.
  boxes.forEach((_, i) => {
    if (!S.isWall(i)) return;
    for (const f of S.faces(i)) {
      dressFacade(S, f, STYLE, i, lowSpec);
      // floor bands every 3.2 m above the string course
      const W = f.t1 - f.t0, tm = (f.t0 + f.t1) / 2;
      for (let y = 6.8; y < f.y1 - 1.5; y += 3.2) placeOrSplit(S, f, "stone", "band", tm, y, W, 0.14, 0.07, 0xc9bfad, 1.5);
    }
  });
  return S.out;
}

export function build(ctx: MapExtrasContext): THREE.Object3D | null {
  const pieces = cityLayout(ctx.map, ctx.lowSpec);
  if (!pieces.length) return null;
  return buildPieces(pieces, "extras:chibre_arena", ctx.theme, ctx.shadows, ctx.lowSpec);
}
